/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React from 'react';
import { Badge, Button, TextStyle, Tooltip } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';

import { EditMajor } from '@shopify/polaris-icons';
import { routes } from 'src/routes';
import { exitSABFullscreen } from 'src/integrations/shopify-app-bridge/fullscreen';

import IconWand from 'src/assets/images/standard-search/wand.svg';

type Props = {
  query: string,
  searchFeatures: Array<Object>,
  history: any,
  enablePPR: boolean
};
type ComposedProps = Props & WithI18nProps;

// Render component
const SearchPreview = (props: ComposedProps) => {
  const { i18n, searchFeatures, query, history, enablePPR } = props;
  if (_.isEmpty(searchFeatures) || searchFeatures.length <= 0) return null;

  const features = searchFeatures.filter(feature => feature.name !== 'ranking' || enablePPR);
  const featuresPreview = features
    .map(feature => {
      const { name, data } = feature;
      switch (name) {
        case 'semantic':
          return {
            name,
            label: (
              <p style={{ display: 'flex', gap: '4px' }}>
                {i18n.translate(`AdminHTML.TestYourSearch.Table.Feature.semantic_Title`)}
                <Badge>
                  <img width={12} height={12} src={IconWand} alt="wand" />
                </Badge>
              </p>
            ),
            detail: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.semantic_Detail', {
              q: i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                term: <TextStyle variation="strong">{`${query}`}</TextStyle>
              })
            }),
            route: routes.semanticSearch.view()
          };
        case 'ranking':
          return {
            name,
            label: (
              <p style={{ display: 'flex', gap: '4px' }}>
                {i18n.translate(`AdminHTML.TestYourSearch.Table.Feature.ranking_Title`)}
                <Badge>
                  <img width={12} height={12} src={IconWand} alt="wand" />
                </Badge>
              </p>
            ),
            detail: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.ranking_Detail'),
            route: routes.productPerformanceRanking.view()
          };
        case 'aiSynonym':
        case 'manualSynonym':
          return {
            name,
            label: (
              <p style={{ display: 'flex', gap: '4px' }}>
                {i18n.translate(`AdminHTML.TestYourSearch.Table.Feature.${name}`)}
                {name === 'aiSynonym' && (
                  <Badge>
                    <img width={12} height={12} src={IconWand} alt="wand" />
                  </Badge>
                )}
              </p>
            ),
            detail: (
              <div className="search-item" key={`${name}`}>
                <p>
                  {data.map((element, indexElement) => (
                    <p key={`term-${indexElement}`}>
                      {i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                        term: (
                          <TextStyle variation="strong">{`${
                            element.searchTerm ? element.searchTerm : query
                          }`}</TextStyle>
                        )
                      })}
                    </p>
                  ))}
                </p>
                <p>
                  {data.map((element, indexElement) => (
                    <p key={`biDirection-${indexElement}`}>{element.biDirection ? '<=>' : '=>'}</p>
                  ))}
                </p>

                <p className="synonyms">
                  {data.map((element, indexElement) => {
                    const { synonyms } = element;
                    return (
                      <p key={`synonyms-${indexElement}`}>
                        {synonyms.map((synonym, index) => (
                          <span className="synonym-item" key={`${name}-item-${index}`}>
                            {synonym ? synonym.trim() : ''}
                          </span>
                        ))}
                      </p>
                    );
                  })}
                </p>
              </div>
            ),
            route: name === 'aiSynonym' ? routes.aiSynonym.view() : routes.synonym.view()
          };
        case 'partialSearch':
          return {
            name,
            label: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.partialSearch_Title'),
            detail: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.partialSearch_Detail', {
              q: i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                term: <TextStyle variation="strong">{`${query}`}</TextStyle>
              })
            })
          };
        case 'typoTolerance':
          return {
            name,
            label: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.typoTolerance_Title'),
            detail: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.typoTolerance_Content', {
              q: i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                term: <TextStyle variation="strong">{`${data.correctQuery}`}</TextStyle>
              })
            })
          };
        case 'searchRange':
          return {
            name,
            label: i18n.translate(
              'AdminHTML.TestYourSearch.Table.Feature.numberAndCharacter_Title'
            ),
            detail: i18n.translate(
              'AdminHTML.TestYourSearch.Table.Feature.numberAndCharacter_Detail',
              {
                range: i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                  term: <TextStyle variation="strong">{`${query}`}</TextStyle>
                })
              }
            )
          };
        case 'stopWord':
          return {
            name,
            label: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.stopWords_Title'),
            detail: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.stopWords_Detail', {
              q: i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.term', {
                term: <TextStyle variation="strong">{`${data.word}`}</TextStyle>
              })
            }),
            route: routes.stopWords.view()
          };
        case 'redirects':
          return {
            name,
            label: i18n.translate('AdminHTML.TestYourSearch.Table.Feature.redirects_Title'),
            detail: (
              <a target="_blank" rel="noopener noreferrer" href={_.get(data, 'redirectTo', '#')}>
                {_.get(data, 'redirectTo', '#')}
              </a>
            ),
            route: routes.redirect.view()
          };
        default:
          return null;
      }
    })
    .filter(e => e);

  const handleEdit = route => {
    exitSABFullscreen();
    history.push(route);
  };
  return featuresPreview.map((feature, index) => (
    <tr
      className={`Polaris-DataTable__TableRow ${
        index === 0 ? 'Polaris-DataTable__TableRow--category' : ''
      }`}
      key={`search-preview-${index}-${feature.name}`}>
      {/* Status */}
      {index === 0 ? (
        <td>
          <p className="category category-search">
            {i18n.translate('AdminHTML.TestYourSearch.Table.Category.search')}
          </p>
        </td>
      ) : (
        <td />
      )}

      <td className={`${index === 0 ? 'Polaris-DataTable__Cell' : ''}`}>{feature.label}</td>
      <td className={`${index === 0 ? 'Polaris-DataTable__Cell' : ''}`}>{feature.detail}</td>
      <td
        className={`${
          index === 0 ? 'Polaris-DataTable__Cell' : ''
        } Polaris-DataTable__Cell--action`}>
        <Tooltip
          content={i18n.translate('AdminHTML.TestYourSearch.Table.editTooltip')}
          preferredPosition="above">
          {feature.route ? (
            <Button size="slim" plain icon={EditMajor} onClick={() => handleEdit(feature.route)} />
          ) : (
            <></>
          )}
        </Tooltip>
      </td>
    </tr>
  ));
};

export default withI18n()(SearchPreview);
